import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  useTheme,
} from '@mui/material';
import { ArrowForward, School, EmojiEvents, Groups, Science, Work, TrendingUp, Business, Star } from '@mui/icons-material';
import HeroCarousel from '../components/HeroCarousel';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  const techClubs = [
    {
      title: 'Embedded Systems & Robotics Club',
      description: 'The Embedded Systems and Robotics Club (ESRC) is a technical club aimed towards conducting workshops from the introductory level and helping students develop their skills in the field of embedded systems and robotics. The club welcomes students from all years and branches to explore their interest in the field.',
      image: '/img5.jpg',
      link: 'https://clubs.mbm.ac.in/tech-clubs/embedded-systems-robotics-club'
    },
    {
      title: 'Google Developer Student Club',
      description: 'GDSC is more than just a club; it\'s a platform that connects students with a diverse range of opportunities in the world of technology. Whether you are a coding enthusiast or someone with a keen interest in the latest developments in the tech industry, GDSC provides a welcoming space for you to explore, grow, and contribute.',
      image: '/img4.png',
      link: 'https://clubs.mbm.ac.in/tech-clubs/google-developer-student-club'
    },
    {
      title: 'Spark Competitive Coding Club',
      description: 'SPARK is a coding community that fosters innovation, organizes contests, and helps students enhance their problem-solving skills. The club regularly conducts coding competitions, hackathons, and interactive sessions with industry experts. SPARK provides a supportive environment to learn, collaborate, and grow your coding abilities.',
      image: '/imgspark.jpg',
      link: 'https://sparkmbmu.netlify.app/'
    }
  ];

  const features = [
    {
      title: 'State-of-the-Art Infrastructure',
      description: 'Modern computer labs and research facilities equipped with the latest technology.',
      icon: '🏛️'
    },
    {
      title: 'Expert Faculty',
      description: 'Learn from experienced professors and industry experts.',
      icon: '👨‍🏫'
    },
    {
      title: 'Industry Partnerships',
      description: 'Strong connections with leading tech companies for internships and placements.',
      icon: '🤝'
    },
    {
      title: 'Technical Clubs',
      description: 'Active participation in various technical clubs like GDSC and Robotics Club.',
      icon: '👥'
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        height: { xs: '65vh', md: '70vh' },
        overflow: 'hidden'
      }}>
        <HeroCarousel />
      </Box>
      <Box sx={{ mb: { xs: 3, md: 5 } }} />

      {/* Welcome Section */}
      <Box sx={{ 
        pt: { xs: 4, md: 6 },
        pb: { xs: 6, md: 8 },
        background: 'linear-gradient(135deg, #23395d 0%, #4062bb 100%)',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        boxShadow: '0 -4px 24px rgba(64,98,187,0.10)',
        position: 'relative',
        color: 'white',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'none',
          opacity: 0,
          zIndex: 0
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" gutterBottom sx={{ 
                fontWeight: 800,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textShadow: '2px 2px 4px rgba(0,0,0,0.18)',
                letterSpacing: '0.5px'
              }}>
                Welcome to CSE Department
              </Typography>
              <Typography variant="h5" sx={{ 
                color: 'rgba(255,255,255,0.92)',
                mb: 4,
                lineHeight: 1.8,
                fontSize: { xs: '1.2rem', md: '1.4rem' },
                fontWeight: 300
              }}>
                Empowering the next generation of computer scientists and engineers through excellence in education, research, and innovation.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/about')}
                sx={{ 
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  background: 'linear-gradient(90deg, #4062bb 60%, #23395d 100%)',
                  color: '#fff',
                  boxShadow: '0 4px 16px rgba(64,98,187,0.18)',
                  borderRadius: 3,
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(90deg, #23395d 60%, #4062bb 100%)',
                    boxShadow: '0 6px 24px rgba(64,98,187,0.22)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s cubic-bezier(.4,2,.6,1)'
                }}
              >
                Explore Programs
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative',
                width: '100%',
                height: { xs: '300px', md: '450px' },
                overflow: 'hidden',
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, rgba(26,35,126,0.2) 0%, rgba(198,40,40,0.2) 100%)',
                  zIndex: 1,
                  pointerEvents: 'none'
                }
              }}>
                <img
                  src="/img3.jpg"
                  alt="CSE Department"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    borderRadius: 16,
                    transform: 'scale(1)',
                    transition: 'transform 0.3s ease',
                    display: 'block',
                    maxWidth: '100%'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ 
        py: { xs: 6, md: 8 },
        my: 4
      }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ 
          mb: 8,
          color: 'primary.main',
          fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.5rem' },
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 80,
            height: 4,
            background: 'linear-gradient(90deg, #1a237e 0%, #c62828 100%)',
            borderRadius: 2
          }
        }}>
          Why Choose Us
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                p: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  '& .feature-icon': {
                    transform: 'scale(1.1)',
                    color: 'primary.main'
                  }
                }
              }}>
                <Typography variant="h1" sx={{ 
                  mb: 3, 
                  fontSize: '3.5rem',
                  transition: 'all 0.3s ease',
                  color: 'text.secondary'
                }} className="feature-icon">
                  {feature.icon}
                </Typography>
                <Typography variant="h5" gutterBottom sx={{ 
                  fontWeight: 700, 
                  color: 'primary.main',
                  mb: 2
                }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Tech Clubs Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'url("/pattern.png")',
          opacity: 0.05,
          zIndex: 0
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" align="center" gutterBottom sx={{ 
            mb: 8,
            color: 'primary.main',
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.5rem' },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 80,
              height: 4,
              background: 'linear-gradient(90deg, #1a237e 0%, #c62828 100%)',
              borderRadius: 2
            }
          }}>
            Technical Clubs
          </Typography>
          <Grid container spacing={4}>
            {techClubs.map((club, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    borderRadius: 4,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                      '& .club-image': {
                        transform: 'scale(1.05)'
                      }
                    }
                  }}
                  onClick={() => window.open(club.link, '_blank')}
                >
                  <Box sx={{ overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="280"
                      image={club.image}
                      alt={club.title}
                      className="club-image"
                      sx={{ 
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        maxWidth: '100%'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography variant="h4" gutterBottom sx={{ 
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 2
                    }}>
                      {club.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph sx={{ 
                      lineHeight: 1.8,
                      mb: 3
                    }}>
                      {club.description}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      endIcon={<ArrowForward />}
                      sx={{ 
                        mt: 2,
                        borderWidth: 2,
                        px: 4,
                        py: 1.5,
                        fontSize: '1.1rem',
                        '&:hover': {
                          borderWidth: 2,
                          background: 'linear-gradient(45deg, #1a237e 30%, #c62828 90%)',
                          color: 'white',
                          transform: 'translateX(8px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: '#ffffff',
        color: '#1F2937',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'none',
          opacity: 0,
          zIndex: 0
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" gutterBottom sx={{ 
                fontWeight: 800,
                color: '#1F2937',
                mb: 4,
                fontSize: { xs: '2rem', md: '2.5rem' },
                textShadow: 'none'
              }}>
                About Our Department
              </Typography>
              <Typography variant="body1" paragraph sx={{ 
                fontSize: '1.2rem',
                lineHeight: 1.8,
                color: '#374151',
                fontWeight: 300
              }}>
                The Computer Science and Engineering Department is committed to excellence in education,
                research, and innovation. We provide a dynamic learning environment that prepares
                students for successful careers in technology and computing.
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: '1.2rem',
                lineHeight: 1.8,
                color: '#374151',
                fontWeight: 300
              }}>
                Our curriculum is designed to provide a strong foundation in computer science
                fundamentals while keeping pace with the latest technological advancements.
                Students have access to modern computing facilities and opportunities to work
                on real-world projects.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                borderRadius: 4,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'none',
                  zIndex: 1,
                  pointerEvents: 'none'
                }
              }}>
                <img
                  src="/img2.jpg"
                  alt="Department Activities"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 16,
                    display: 'block',
                    maxWidth: '100%'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 
